import { useState, useEffect, useRef } from 'react';

/**
 * Hook to detect if the mobile browser URL bar is visible at the bottom of the screen
 * Specifically targeting iOS Safari and Chrome
 */
const useBottomUrlBarDetection = () => {
  const [isBottomUrlBarVisible, setIsBottomUrlBarVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  
  // IMPORTANT: Set to false to use actual detection logic
  const FORCE_DETECTION = false;
  
  // Height tracking references 
  const maxHeightRef = useRef<number>(0);
  const minHeightRef = useRef<number>(0);
  const initialHeightRef = useRef<number | null>(null);
  
  // For iOS, we can use more specific detection techniques
  const isIOS = typeof navigator !== 'undefined' && /iphone|ipod|ipad/.test(navigator.userAgent.toLowerCase());
  
  useEffect(() => {
    // Check if the device is mobile
    const isMobileDevice = typeof navigator !== 'undefined' && 
      /iphone|ipod|ipad|android|mobile|tablet/.test(navigator.userAgent.toLowerCase());
    setIsMobile(isMobileDevice);
    
    // Early exit if not on mobile
    if (!isMobileDevice && !FORCE_DETECTION) return;
    
    // For testing - force detection
    if (FORCE_DETECTION) {
      setIsBottomUrlBarVisible(true);
      return;
    }
    
    // Set initial height
    if (initialHeightRef.current === null) {
      initialHeightRef.current = window.innerHeight;
      maxHeightRef.current = window.innerHeight;
      minHeightRef.current = window.innerHeight;
    }
    
    // Function to check URL bar visibility
    const checkUrlBar = () => {
      const currentHeight = window.innerHeight;
      
      // Track max/min heights we've seen
      if (currentHeight > maxHeightRef.current) {
        maxHeightRef.current = currentHeight;
      }
      
      if (currentHeight < minHeightRef.current) {
        minHeightRef.current = currentHeight;
      }
      
      if (isIOS) {
        // On iOS Safari, the URL bar showing/hiding causes significant height changes
        const heightDifference = maxHeightRef.current - currentHeight;
        
        // iOS URL bars are typically 40-60px, use 30px as threshold
        const newUrlBarVisible = heightDifference > 30;
        
        // Only update state if there's been a change
        if (isBottomUrlBarVisible !== newUrlBarVisible) {
          setIsBottomUrlBarVisible(newUrlBarVisible);
          
          console.log('URL Bar State Changed:', {
            isNowVisible: newUrlBarVisible,
            heightDifference,
            currentHeight,
            maxHeight: maxHeightRef.current,
            minHeight: minHeightRef.current,
          });
        }
      } else if (isMobileDevice) {
        // Android detection uses a different approach
        const viewportRatio = currentHeight / window.screen.height;
        const newUrlBarVisible = viewportRatio < 0.9;
        
        if (isBottomUrlBarVisible !== newUrlBarVisible) {
          setIsBottomUrlBarVisible(newUrlBarVisible);
        }
      }
    };
    
    // Check immediately
    checkUrlBar();
    
    // Events that can trigger URL bar visibility changes
    const scrollHandler = () => {
      // Delay check slightly to let UI settle
      setTimeout(checkUrlBar, 100);
    };
    
    // When window resizes (which happens when URL bar shows/hides)
    const resizeHandler = () => {
      checkUrlBar();
    };
    
    // Set up all event handlers
    window.addEventListener('resize', resizeHandler);
    window.addEventListener('scroll', scrollHandler);
    window.addEventListener('touchend', scrollHandler);
    window.addEventListener('orientationchange', () => {
      // Reset heights after orientation change
      initialHeightRef.current = null;
      maxHeightRef.current = window.innerHeight;
      minHeightRef.current = window.innerHeight;
      
      // Check after a delay to let things settle
      setTimeout(checkUrlBar, 300);
    });
    
    // Clean up
    return () => {
      window.removeEventListener('resize', resizeHandler);
      window.removeEventListener('scroll', scrollHandler);
      window.removeEventListener('touchend', scrollHandler);
      window.removeEventListener('orientationchange', resizeHandler);
    };
  }, [isIOS, isBottomUrlBarVisible, FORCE_DETECTION]);

  return { isBottomUrlBarVisible, isMobile };
};

export default useBottomUrlBarDetection; 